import { X } from '@untitledui/icons';

import { Button } from '@/lib/base/buttons/button';

interface ApproveAllControlProps {
  /** Handler for the primary "Approve" segment */
  onApprove: () => void;
  /** Handler for the trailing dismiss (✕) segment */
  onDismiss: () => void;
  /** Primary segment label ("Approve all" / "Approve selected") */
  label?: string;
  /** Visible label for the dismiss segment ("Dismiss all" / "Dismiss selected"); also serves as the accessible name */
  dismissLabel?: string;
  /** Disables both segments (e.g. during an in-flight bulk operation) */
  disabled?: boolean;
}

// Shared overrides for both segments: strip all Button `sm` chrome so the
// outer container controls shape. `before:!hidden` kills the inner-border
// pseudo; `!rounded-none` defers rounding to the container; `!font-medium`
// reduces the visual weight of `sm`'s default `font-semibold`.
const SEGMENT =
  '!h-auto !rounded-none !font-medium !py-[2px] !text-[12px] !leading-[18px] before:!hidden';

/**
 * Segmented bulk-action pill for priority group headers: a primary "Approve
 * all" segment and a trailing labeled "✕" dismiss segment joined inside one
 * bordered container. Built on the design-system {@link Button} with `tertiary` color
 * (zero per-button chrome) so the container provides the single unified
 * border — matching the sort toggle pattern in ProposalListPanel. The dismiss
 * segment tints red on hover to signal discard.
 */
export function ApproveAllControl({
  onApprove,
  onDismiss,
  label = 'Approve all',
  dismissLabel = 'Dismiss all',
  disabled = false,
}: ApproveAllControlProps) {
  return (
    <div className='inline-flex items-stretch overflow-hidden rounded-md border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)]'>
      <Button
        color='tertiary'
        size='sm'
        isDisabled={disabled}
        onClick={onApprove}
        className={`${SEGMENT} !px-[8px]`}
      >
        {label}
      </Button>
      <span
        aria-hidden='true'
        className='w-px self-stretch bg-[var(--color-border-secondary)]'
      />
      <Button
        color='tertiary'
        size='sm'
        isDisabled={disabled}
        onClick={onDismiss}
        aria-label={dismissLabel}
        title={dismissLabel}
        iconLeading={<X className='!size-3 !block' strokeWidth={2.5} />}
        className={`${SEGMENT} !px-[8px] hover:!bg-error-50 hover:!text-error-500 active:!bg-error-100`}
      >
        {dismissLabel}
      </Button>
    </div>
  );
}
